import React, { useCallback, useState, useContext } from 'react';
import classnames from 'clsx';
import { Link } from 'react-router-dom';
import { Popover } from '@mantine/core';

import { RenderItemContentPropType } from 'ui-components/prop-types/tree-navigation';
import { FlexRow } from 'ui-components/components/flex';
import { TOOL_TIP_POSITIONS } from 'ui-components/services/tooltip-service';

import { TreeItemCount } from './tree-item-count';
import { ExpanderIcon } from './expander-icon';
import { ItemIcon } from './item-icon';
import { Label } from './label';
import { LockIconWithTooltip } from '../../../../feature-gates/lock-icon-with-tooltip';
import { messages } from '../../../../feature-gates/messages';

import { emptyObject } from 'ui-components/default-props/empty';
import { noop } from '../../../default-props/noop';
import style from '../tree-navigation.module.less';
import { NavItemPopoverContext } from '../../../../contexts/nav-item-popover-context';
import { popoverClassNames } from '../../../../components/seller-recommendations/components/recommendation-popover-card/recommendation-popover-card';

export const TreeItemContent = ({
  item = emptyObject,
  isHighlighted = false,
  buildTagProps = noop,
  subItemIndex = null,
  isItemSearchResult = false,
  shouldRenderAnIcon = false,
  onItemSelected = noop,
  onExpandClick = noop,
  tabIndex = 0,
}) => {
  const { selected, items, label, count, expanded, isLocked = false, id } = item;

  const [shouldShowFocusedState, setShouldShowFocusedState] = useState(true);
  const resetShouldShowFocusedState = useCallback(() => setShouldShowFocusedState(true), []);

  const popoverContext = useContext(NavItemPopoverContext);
  const popoverConfig = popoverContext?.popovers[id] ?? null;

  const isSubItem = Number.isInteger(subItemIndex);

  const onSelected = useCallback(
    (e) => {
      setShouldShowFocusedState(false);
      onItemSelected(item, e);
    },
    [item, onItemSelected],
  );

  const onExpand = useCallback(() => {
    onExpandClick(item);
  }, [item, onExpandClick]);

  const isExpandable = items && items.length > 0;

  const wrapperClasses = classnames(style['tree-navigation-item-row'], {
    [style['is-expandable']]: isExpandable,
    [style['is-subitem']]: isSubItem,
    [style['is-selected']]: selected && !isHighlighted,
    [style['is-highlighted']]: isHighlighted, // search results
    [style['is-selected-and-highlighted']]: selected && isHighlighted, // search results
    [style['has-no-icon']]: !shouldRenderAnIcon && !isExpandable,
    [style['hide-keyboard-focus']]: !shouldShowFocusedState,
    [style['is-locked']]: isLocked,
  });

  const classes = classnames(style['tree-navigation-item-content'], {
    [style.expanded]: expanded,
    [style.expandable]: isExpandable,
  });

  const treeItemContent = (
    <div aria-expanded={expanded} aria-selected={selected} className={wrapperClasses} onBlur={resetShouldShowFocusedState} role='treeitem'>
      <FlexRow key={subItemIndex} className={classes} tabIndex={tabIndex} testId='tree-item-content'>
        <Link className={style.label} onClick={onSelected} tabIndex={tabIndex} {...buildTagProps(item)}>
          <ExpanderIcon isItemSearchResult={isItemSearchResult} onExpand={onExpand} />
          <ItemIcon item={item} shouldRenderAnIcon={shouldRenderAnIcon} />
          <Label count={count} label={label} />
        </Link>
        {isLocked && (
          <LockIconWithTooltip
            iconClass={style.icon}
            isClickable
            position={TOOL_TIP_POSITIONS.bottomLeft}
            tooltipMessageDescriptor={item.lockTooltipMessage || messages.featureNotAvailableOnCurrentPlan}
          />
        )}
        <TreeItemCount count={count} onClick={onSelected} />
      </FlexRow>
    </div>
  );

  if (popoverConfig) {
    return (
      <Popover opened={popoverConfig.isOpen} position='right' offset={12} withArrow withinPortal shadow='md' classNames={popoverClassNames}>
        <Popover.Target>{treeItemContent}</Popover.Target>
        <Popover.Dropdown>{popoverConfig.content}</Popover.Dropdown>
      </Popover>
    );
  }

  return treeItemContent;
};

TreeItemContent.propTypes = RenderItemContentPropType;
